import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ChevronLeft,
  ArrowUpDown,
  Award,
  DollarSign,
  Clock,
  TrendingDown,
  Check,
  Star,
  Ship,
  Plane,
  Truck,
  Train,
  AlertTriangle,
  History,
  MessageSquare,
  Lightbulb,
  CheckCircle2,
  Shield,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { MetricCard } from '@/components/common';
import {
  QuoteVersionHistoryDialog,
  StartNegotiationDialog,
  NegotiationPanel,
} from '@/components/tms';
import {
  rfqApi,
  FreightRFQ,
  FreightRFQLine,
  FreightRFQResponse,
  ShipmentType,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';

const shipmentTypeIcons: Record<ShipmentType, React.ReactNode> = {
  OCEAN_FCL: <Ship className="w-5 h-5" />,
  OCEAN_LCL: <Ship className="w-5 h-5" />,
  AIR: <Plane className="w-5 h-5" />,
  FTL: <Truck className="w-5 h-5" />,
  LTL: <Truck className="w-5 h-5" />,
  RAIL: <Train className="w-5 h-5" />,
  BREAK_BULK: <Ship className="w-5 h-5" />,
  INTERMODAL: <Truck className="w-5 h-5" />,
};

const statusColors: Record<string, string> = {
  Draft: 'bg-gray-100 text-gray-600 border-gray-200',
  Open: 'bg-blue-100 text-blue-600 border-blue-200',
  Closed: 'bg-amber-100 text-amber-600 border-amber-200',
  Awarded: 'bg-emerald-100 text-emerald-600 border-emerald-200',
  Cancelled: 'bg-red-100 text-red-600 border-red-200',
};

export default function FreightRFQCompare() {
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [rfq, setRfq] = useState<FreightRFQ | null>(null);
  const [lines, setLines] = useState<FreightRFQLine[]>([]);
  const [responses, setResponses] = useState<FreightRFQResponse[]>([]);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [awarding, setAwarding] = useState<string | null>(null);

  // Quote Version History Dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [historyCarrierId, setHistoryCarrierId] = useState<string | null>(null);

  // Negotiation Dialog and Panel
  const [negotiationDialogOpen, setNegotiationDialogOpen] = useState(false);
  const [negotiationPanelOpen, setNegotiationPanelOpen] = useState(false);
  const [negotiatingResponse, setNegotiatingResponse] = useState<FreightRFQResponse | null>(null);

  useEffect(() => {
    if (id) {
      loadRFQ();
    }
  }, [id]);

  useEffect(() => {
    const lineParam = searchParams.get('line');
    if (lineParam && lines.some((l) => l.id === lineParam)) {
      setSelectedLineId(lineParam);
    } else if (lines.length > 0 && !selectedLineId) {
      setSelectedLineId(lines[0].id);
    }
  }, [lines, searchParams]);

  useEffect(() => {
    if (selectedLineId) {
      loadLineResponses(selectedLineId);
      setSearchParams({ line: selectedLineId });
    }
  }, [selectedLineId]);

  const loadRFQ = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await rfqApi.getById(id);
      setRfq(data);
      setLines(data.lines || []);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const loadLineResponses = async (lineId: string) => {
    if (!id) return;
    try {
      const data = await rfqApi.compareQuotes(id, lineId);
      const sorted = data.quotes.sort(
        (a, b) => (a.totalRate || a.baseRate) - (b.totalRate || b.baseRate)
      );
      setResponses(sorted);
    } catch (error) {
      console.error('Failed to load responses:', error);
      setResponses([]);
    }
  };

  const handleAwardLine = async (responseId: string) => {
    if (!id || !selectedLineId) return;
    try {
      setAwarding(responseId);
      await rfqApi.createAward(id, {
        lineId: selectedLineId,
        responseId,
        awardedRate: responses.find((r) => r.id === responseId)?.totalRate || 0,
      });
      toast.success('Line awarded successfully');
      await loadRFQ();
      await loadLineResponses(selectedLineId);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setAwarding(null);
    }
  };

  const selectedLine = lines.find((l) => l.id === selectedLineId);
  const lowestRate =
    responses.length > 0
      ? Math.min(...responses.map((r) => r.totalRate || r.baseRate))
      : null;
  const avgRate =
    responses.length > 0
      ? responses.reduce((sum, r) => sum + (r.totalRate || r.baseRate), 0) /
        responses.length
      : null;
  const targetRate = selectedLine?.targetRate;

  // Generate L1 explanation
  const getL1Explanation = () => {
    if (responses.length === 0) return null;

    const l1Response = responses[0];
    const l1Total = l1Response.totalRate || l1Response.baseRate;
    const l1Carrier = l1Response.invitation?.carrier;
    const reasons: {
      icon: React.ReactNode;
      title: string;
      detail: string;
      highlight?: boolean;
    }[] = [];

    if (responses.length > 1) {
      const secondLowest = responses[1].totalRate || responses[1].baseRate;
      const savings = secondLowest - l1Total;
      const savingsPercent = ((savings / secondLowest) * 100).toFixed(1);
      reasons.push({
        icon: <DollarSign className="w-4 h-4" />,
        title: 'Best Value',
        detail: `Lowest total cost at $${l1Total.toLocaleString()} - saves $${savings.toLocaleString()} (${savingsPercent}%) vs next bid`,
        highlight: true,
      });
    } else {
      reasons.push({
        icon: <DollarSign className="w-4 h-4" />,
        title: 'Only Quote',
        detail: `Submitted at $${l1Total.toLocaleString()}`,
      });
    }

    if (targetRate && l1Total < targetRate) {
      const savingsVsTarget = (
        ((targetRate - l1Total) / targetRate) *
        100
      ).toFixed(1);
      reasons.push({
        icon: <TrendingDown className="w-4 h-4" />,
        title: 'Below Target',
        detail: `${savingsVsTarget}% under your target rate of $${targetRate.toLocaleString()}`,
        highlight: true,
      });
    }

    const minTransit = Math.min(
      ...responses
        .filter((r) => r.transitDaysOffered)
        .map((r) => r.transitDaysOffered!)
    );
    if (
      l1Response.transitDaysOffered &&
      l1Response.transitDaysOffered === minTransit &&
      responses.length > 1
    ) {
      reasons.push({
        icon: <Zap className="w-4 h-4" />,
        title: 'Fastest Transit',
        detail: `${l1Response.transitDaysOffered} days transit - quickest among all quotes`,
      });
    } else if (l1Response.transitDaysOffered) {
      reasons.push({
        icon: <Clock className="w-4 h-4" />,
        title: 'Transit Time',
        detail: `${l1Response.transitDaysOffered} days transit offered`,
      });
    }

    if (l1Response.isContractRate) {
      reasons.push({
        icon: <Shield className="w-4 h-4" />,
        title: 'Contract Rate',
        detail: 'Pre-negotiated contract rate with stable pricing guarantees',
      });
    }

    const hasChildRates =
      l1Response.childRates && l1Response.childRates.length > 0;
    if (hasChildRates) {
      reasons.push({
        icon: <CheckCircle2 className="w-4 h-4" />,
        title: 'Transparent Pricing',
        detail: `All ${l1Response.childRates!.length} accessorial charges itemized for full cost visibility`,
      });
    }

    return {
      carrier: l1Carrier?.name || 'Unknown',
      carrierCode: l1Carrier?.code,
      totalRate: l1Total,
      reasons,
    };
  };

  const l1Explanation = getL1Explanation();

  if (loading || !rfq) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={() => navigate(`/tms/rfq/${id}`)}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to RFQ
          </Button>
        </div>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigate(`/tms/rfq/${id}`)}
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3 mb-1">
              <span className="font-mono text-sm text-muted-foreground">
                {rfq.rfqNumber}
              </span>
              <span
                className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[rfq.status]}`}
              >
                {rfq.status}
              </span>
              {rfq.shipmentType && (
                <span className="flex items-center gap-1 px-2 py-0.5 bg-muted rounded text-xs">
                  {shipmentTypeIcons[rfq.shipmentType]}
                  {rfq.shipmentType.replace('_', ' ')}
                </span>
              )}
            </div>
            <h1 className="text-2xl font-bold">Quote Comparison</h1>
            <p className="text-muted-foreground mt-1">{rfq.title}</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => setNegotiationPanelOpen(true)}>
          <MessageSquare className="w-4 h-4 mr-2" />
          View Negotiations
        </Button>
      </div>

      {/* Lane Selector */}
      <div className="bg-card rounded-xl border p-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium">Select Lane:</label>
          <Select
            value={selectedLineId || ''}
            onValueChange={setSelectedLineId}
          >
            <SelectTrigger className="w-full max-w-md">
              <SelectValue placeholder="Select a lane" />
            </SelectTrigger>
            <SelectContent>
              {lines.map((line) => (
                <SelectItem key={line.id} value={line.id}>
                  Lane #{line.lineNumber}: {line.originCity}, {line.originState}{' '}
                  {line.originPort && `(${line.originPort})`} → {line.destCity},{' '}
                  {line.destState} {line.destPort && `(${line.destPort})`}
                  {line.award && ' [Awarded]'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPIs */}
      {selectedLine && (
        <div className="grid grid-cols-5 gap-4">
          <MetricCard
            label="Quotes Received"
            value={responses.length}
            icon={<DollarSign className="w-5 h-5" />}
          />
          <MetricCard
            label="Lowest Quote"
            value={lowestRate ? `$${lowestRate.toLocaleString()}` : '-'}
            icon={<TrendingDown className="w-5 h-5" />}
          />
          <MetricCard
            label="Average Quote"
            value={avgRate ? `$${avgRate.toLocaleString()}` : '-'}
            icon={<ArrowUpDown className="w-5 h-5" />}
          />
          <MetricCard
            label="Target Rate"
            value={targetRate ? `$${targetRate.toLocaleString()}` : '-'}
            icon={<Star className="w-5 h-5" />}
          />
          <MetricCard
            label="Savings vs Target"
            value={
              targetRate && lowestRate
                ? `${(((targetRate - lowestRate) / targetRate) * 100).toFixed(1)}%`
                : '-'
            }
            icon={<Check className="w-5 h-5" />}
          />
        </div>
      )}

      {/* Why L1 Explainer */}
      {l1Explanation && responses.length > 0 && !selectedLine?.award && (
        <div className="bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30 rounded-xl border border-emerald-200 dark:border-emerald-800 overflow-hidden">
          <div className="px-6 py-4 border-b border-emerald-200 dark:border-emerald-800 bg-emerald-100/50 dark:bg-emerald-900/30">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-emerald-500 text-white">
                <Lightbulb className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-semibold text-emerald-900 dark:text-emerald-100 flex items-center gap-2">
                  Why {l1Explanation.carrier} is ranked L1
                  <Badge className="bg-emerald-500 text-white hover:bg-emerald-600">
                    <Star className="w-3 h-3 mr-1" />
                    Recommended
                  </Badge>
                </h3>
                <p className="text-sm text-emerald-700 dark:text-emerald-300">
                  Best value at ${l1Explanation.totalRate.toLocaleString()} for
                  this lane
                </p>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {l1Explanation.reasons.map((reason, idx) => (
                <div
                  key={idx}
                  className={`flex items-start gap-3 p-3 rounded-lg ${
                    reason.highlight
                      ? 'bg-emerald-100 dark:bg-emerald-900/50 border border-emerald-200 dark:border-emerald-700'
                      : 'bg-white dark:bg-card border'
                  }`}
                >
                  <div
                    className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${
                      reason.highlight
                        ? 'bg-emerald-500 text-white'
                        : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {reason.icon}
                  </div>
                  <div>
                    <div
                      className={`font-medium ${reason.highlight ? 'text-emerald-900 dark:text-emerald-100' : ''}`}
                    >
                      {reason.title}
                    </div>
                    <div
                      className={`text-sm ${reason.highlight ? 'text-emerald-700 dark:text-emerald-300' : 'text-muted-foreground'}`}
                    >
                      {reason.detail}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 pt-4 border-t border-emerald-200 dark:border-emerald-700 flex items-center justify-between">
              <p className="text-sm text-emerald-700 dark:text-emerald-300">
                <strong>Recommendation:</strong> Award this bid to maximize
                savings while meeting transit requirements.
              </p>
              {!selectedLine?.award && rfq.status !== 'Awarded' && (
                <Button
                  onClick={() => handleAwardLine(responses[0].id)}
                  disabled={awarding === responses[0].id}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {awarding === responses[0].id ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <Award className="w-4 h-4 mr-2" />
                      Award to {l1Explanation.carrier}
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Comparison Table */}
      <div className="bg-card rounded-xl border overflow-hidden">
        <div className="px-6 py-4 border-b">
          <h2 className="font-semibold">
            Quote Comparison - Lane #{selectedLine?.lineNumber}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {selectedLine?.originCity} → {selectedLine?.destCity} •{' '}
            {responses.length} quote(s)
          </p>
        </div>

        {responses.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50 text-left">
                  <th className="px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Rank
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Carrier
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                    Base Rate
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                    Child Rates
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right">
                    Total Rate
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-center">
                    Transit Days
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-center">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {responses.map((response, index) => {
                  const totalRate = response.totalRate || response.baseRate;
                  const isLowest = totalRate === lowestRate;
                  const childRatesTotal =
                    response.childRates?.reduce(
                      (sum, cr) => sum + cr.amount,
                      0
                    ) || 0;
                  const isBelowTarget = targetRate && totalRate < targetRate;
                  const isAwarded =
                    selectedLine?.award?.responseId === response.id;

                  return (
                    <tr
                      key={response.id}
                      className={`${isLowest ? 'bg-emerald-50/50 dark:bg-emerald-950/20' : ''} ${isAwarded ? 'bg-blue-50/50 dark:bg-blue-950/20' : ''} hover:bg-muted/50`}
                    >
                      {/* Rank */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {index === 0 && (
                            <span className="flex items-center justify-center w-6 h-6 rounded-full bg-emerald-100 dark:bg-emerald-900 text-emerald-600 dark:text-emerald-400">
                              <Star className="w-3.5 h-3.5" />
                            </span>
                          )}
                          <span
                            className={`font-medium ${index === 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground'}`}
                          >
                            #{index + 1}
                          </span>
                        </div>
                      </td>

                      {/* Carrier */}
                      <td className="px-6 py-4">
                        <div className="space-y-1">
                          <div className="font-medium">
                            {response.invitation?.carrier?.name || 'Unknown'}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {response.invitation?.carrier?.code}
                          </div>
                        </div>
                      </td>

                      {/* Base Rate */}
                      <td className="px-6 py-4 text-right">
                        <span className="font-medium">
                          ${response.baseRate.toLocaleString()}
                        </span>
                        <div className="text-xs text-muted-foreground">
                          {response.currency || 'USD'}
                        </div>
                      </td>

                      {/* Child Rates */}
                      <td className="px-6 py-4 text-right">
                        {childRatesTotal > 0 ? (
                          <div>
                            <span className="font-medium">
                              +${childRatesTotal.toLocaleString()}
                            </span>
                            <div className="text-xs text-muted-foreground">
                              {response.childRates?.length} charge(s)
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>

                      {/* Total Rate */}
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <span
                            className={`font-bold text-lg ${isLowest ? 'text-emerald-600 dark:text-emerald-400' : ''}`}
                          >
                            ${totalRate.toLocaleString()}
                          </span>
                          {isBelowTarget && (
                            <TrendingDown className="w-4 h-4 text-emerald-500" />
                          )}
                        </div>
                        {targetRate && (
                          <div
                            className={`text-xs ${isBelowTarget ? 'text-emerald-600' : 'text-amber-600'}`}
                          >
                            {isBelowTarget ? '-' : '+'}
                            {Math.abs(
                              ((totalRate - targetRate) / targetRate) * 100
                            ).toFixed(1)}
                            % vs target
                          </div>
                        )}
                      </td>

                      {/* Transit Days */}
                      <td className="px-6 py-4 text-center">
                        {response.transitDaysOffered ? (
                          <div className="flex items-center justify-center gap-1">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <span className="font-medium">
                              {response.transitDaysOffered}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              days
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <Badge
                            variant="outline"
                            className={
                              response.quoteStatus === 'Submitted'
                                ? 'bg-blue-50 text-blue-600 border-blue-200'
                                : response.quoteStatus === 'Accepted'
                                  ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                                  : ''
                            }
                          >
                            {response.quoteStatus}
                          </Badge>
                          {isAwarded && (
                            <Badge className="bg-emerald-100 text-emerald-600 border-emerald-200">
                              <Award className="w-3 h-3 mr-1" />
                              Awarded
                            </Badge>
                          )}
                          {response.isContractRate && (
                            <Badge
                              variant="outline"
                              className="bg-purple-50 text-purple-600 border-purple-200"
                            >
                              Contract Rate
                            </Badge>
                          )}
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            title="View quote history"
                            onClick={() => {
                              setHistoryCarrierId(response.invitation?.carrier?.id || null);
                              setHistoryDialogOpen(true);
                            }}
                          >
                            <History className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            title="Negotiate rate"
                            onClick={() => {
                              setNegotiatingResponse(response);
                              setNegotiationDialogOpen(true);
                            }}
                            disabled={!!selectedLine?.award || rfq.status === 'Awarded'}
                          >
                            <MessageSquare className="w-4 h-4" />
                          </Button>
                          {!selectedLine?.award &&
                            rfq.status !== 'Awarded' && (
                              <Button
                                size="sm"
                                onClick={() => handleAwardLine(response.id)}
                                disabled={awarding === response.id}
                              >
                                {awarding === response.id ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                ) : (
                                  <>
                                    <Award className="w-4 h-4 mr-1" />
                                    Award
                                  </>
                                )}
                              </Button>
                            )}
                          {isAwarded && (
                            <span className="text-sm text-emerald-600 font-medium">
                              Awarded
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center">
            <AlertTriangle className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No Quotes Received</h3>
            <p className="text-muted-foreground">
              No carriers have submitted quotes for this lane yet.
            </p>
          </div>
        )}
      </div>

      {/* Lane Summary */}
      <div className="bg-card rounded-xl border p-6">
        <h2 className="font-semibold mb-4">All Lanes Summary</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {lines.map((line) => {
            const isSelected = line.id === selectedLineId;
            return (
              <button
                key={line.id}
                onClick={() => setSelectedLineId(line.id)}
                className={`text-left p-4 rounded-lg border-2 transition-all ${
                  isSelected
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-muted-foreground/30'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium">Lane #{line.lineNumber}</span>
                  {line.award && (
                    <Badge className="bg-emerald-100 text-emerald-600">
                      <Award className="w-3 h-3 mr-1" />
                      Awarded
                    </Badge>
                  )}
                </div>
                <div className="text-sm text-muted-foreground">
                  {line.originCity} → {line.destCity}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {line.mode} • {line.responses?.length || 0} quotes
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Quote Version History Dialog */}
      {id && selectedLineId && historyCarrierId && (
        <QuoteVersionHistoryDialog
          open={historyDialogOpen}
          onOpenChange={setHistoryDialogOpen}
          rfqId={id}
          lineId={selectedLineId}
          carrierId={historyCarrierId}
          lineInfo={
            selectedLine && selectedLine.originCity && selectedLine.destCity
              ? {
                  lineNumber: selectedLine.lineNumber,
                  originCity: selectedLine.originCity,
                  destCity: selectedLine.destCity,
                }
              : undefined
          }
        />
      )}

      {/* Start Negotiation Dialog */}
      {id && selectedLineId && negotiatingResponse && (
        <StartNegotiationDialog
          open={negotiationDialogOpen}
          onOpenChange={(open) => {
            setNegotiationDialogOpen(open);
            if (!open) setNegotiatingResponse(null);
          }}
          rfqId={id}
          lineId={selectedLineId}
          responseId={negotiatingResponse.id}
          carrierName={negotiatingResponse.invitation?.carrier?.name || 'Unknown Carrier'}
          currentRate={negotiatingResponse.totalRate || negotiatingResponse.baseRate}
          currency={negotiatingResponse.currency || 'USD'}
          onSuccess={() => {
            loadLineResponses(selectedLineId);
          }}
        />
      )}

      {/* Negotiation Panel */}
      {id && (
        <NegotiationPanel
          open={negotiationPanelOpen}
          onOpenChange={setNegotiationPanelOpen}
          rfqId={id}
          onNegotiationUpdate={() => {
            if (selectedLineId) {
              loadLineResponses(selectedLineId);
            }
          }}
        />
      )}
    </div>
  );
}
